import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, DataTable, Metric, Select, StatusBadge, formatDate } from "@odingard/design-system";
import { EmptyState, LoadingState } from "@odingard/design-system";
import type { Obligation } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";
import { CaiError } from "../../components/CaiError";

function isOverdue(o: Obligation) {
  return o.status !== "completed" && !!o.dueDate && new Date(o.dueDate).getTime() < Date.now();
}

export function CaiObligations() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(() => api.obligations(orgId), [orgId]);
  const [status, setStatus] = useState("");

  const all = useMemo(() => state.data?.obligations ?? [], [state.data]);
  const rows = useMemo(() => all.filter((o) => !status || o.status === status), [all, status]);
  const overdue = all.filter(isOverdue).length;

  const cols = [
    { key: "desc", header: "Obligation", render: (o: Obligation) => <strong>{o.description}</strong> },
    {
      key: "contract",
      header: "Contract",
      render: (o: Obligation) => <Link to={`/studio/contract-ai/contracts/${o.contractId}`}>{o.contractTitle ?? o.contractId}</Link>,
    },
    { key: "owner", header: "Owner", render: (o: Obligation) => o.owner ?? "Unassigned" },
    {
      key: "due",
      header: "Due",
      render: (o: Obligation) =>
        o.dueDate ? <span style={isOverdue(o) ? { color: "var(--ods-color-danger)", fontWeight: 700 } : undefined}>{formatDate(o.dueDate)}</span> : "Not set",
    },
    { key: "cal", header: "Calendar", render: (o: Obligation) => (o.calendarLinked ? <Badge tone="info">Linked</Badge> : "Not linked") },
    { key: "status", header: "Status", render: (o: Obligation) => <StatusBadge status={o.status} /> },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Obligations</h1>
          <p>Commitments extracted across your portfolio.</p>
        </div>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="completed">Completed</option>
        </Select>
      </div>

      {state.loading && <LoadingState />}
      {state.error && <CaiError error={state.error} onRetry={state.reload} />}
      {state.data && (
        <>
          <div className="metric-grid">
            <div className="metric-card">
              <Metric value={all.length} label="Total obligations" />
            </div>
            <div className="metric-card">
              <Metric value={overdue} label="Overdue" tone="var(--ods-color-danger)" />
            </div>
            <div className="metric-card">
              <Metric value={all.filter((o) => o.calendarLinked).length} label="Calendar linked" />
            </div>
          </div>
          {rows.length === 0 ? (
            <EmptyState icon="✅" title="No obligations" description="No obligations match the current filter." />
          ) : (
            <DataTable columns={cols} rows={rows} caption="Obligations" />
          )}
        </>
      )}
    </>
  );
}
